import { useState, useCallback, useRef, useEffect, type RefObject } from 'react';
import type { CampaignChatMessage, FlowStep } from '../types';

interface UseCampaignMessagesResult {
  messages: CampaignChatMessage[];
  showOptions: boolean;
  isTyping: boolean;
  addAiMessage: (text: string, step: FlowStep) => void;
  addUserMessage: (text: string, step: FlowStep) => void;
  onAiMessageComplete: () => void;
  scrollToUserMessage: () => void;
  chatContainerRef: RefObject<HTMLDivElement>;
}

/**
 * Hook for managing campaign chat messages with typewriter support.
 * Handles message history, animation state, and auto-scrolling.
 */
export function useCampaignMessages(tableName: string): UseCampaignMessagesResult {
  const [messages, setMessages] = useState<CampaignChatMessage[]>([]);
  const [showOptions, setShowOptions] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  /**
   * Scroll to position the last user message near the top
   */
  const scrollToUserMessage = useCallback(() => {
    requestAnimationFrame(() => {
      if (chatContainerRef.current) {
        const container = chatContainerRef.current;
        const userMessages = container.querySelectorAll('[data-user-message]');
        const lastUserMessage = userMessages[userMessages.length - 1] as HTMLElement;

        if (lastUserMessage) {
          const containerRect = container.getBoundingClientRect();
          const messageRect = lastUserMessage.getBoundingClientRect();
          const scrollOffset = messageRect.top - containerRect.top - 16;

          container.scrollTo({
            top: container.scrollTop + scrollOffset,
            behavior: 'smooth',
          });
        }
      }
    });
  }, []);

  /**
   * Add AI message with typewriter animation
   */
  const addAiMessage = useCallback((text: string, step: FlowStep) => {
    const id = `ai-${Date.now()}`;
    setShowOptions(false);
    setIsTyping(true);

    setMessages((prev) => [
      ...prev,
      {
        id,
        type: 'ai',
        content: text,
        step,
        isAnimating: true,
      },
    ]);
  }, []);

  /**
   * Add user message (instant, no animation)
   */
  const addUserMessage = useCallback(
    (text: string, step: FlowStep) => {
      const id = `user-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id,
          type: 'user',
          content: text,
          step,
        },
      ]);
      scrollToUserMessage();
    },
    [scrollToUserMessage]
  );

  /**
   * Called when AI message animation completes
   */
  const onAiMessageComplete = useCallback(() => {
    setShowOptions(true);
    setIsTyping(false);

    // Update message to not animating
    setMessages((prev) =>
      prev.map((m) => (m.isAnimating ? { ...m, isAnimating: false } : m))
    );
  }, []);

  /**
   * Initialize with first message (only once)
   */
  useEffect(() => {
    if (!hasInitialized.current && messages.length === 0) {
      hasInitialized.current = true;
      addAiMessage(
        `I'll help you set up an outreach campaign for your dataset. Which leads do you want to include?`,
        'lead_selection'
      );
    }
  }, [tableName, messages.length, addAiMessage]);

  return {
    messages,
    showOptions,
    isTyping,
    addAiMessage,
    addUserMessage,
    onAiMessageComplete,
    scrollToUserMessage,
    chatContainerRef,
  };
}
